/** @format */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchUserData } from 'state/slice/userSlice';
import { AuthService } from '../services/authService';
import { validatePassword } from '../utils/passwordRequirements';
import { handleTokenStorage, getNavigationPath } from '../utils/authUtils';

interface UpdatePasswordSession {
  username: string;
  session: string;
}

interface UseUpdatePasswordReturn {
  newPassword: string;
  confirmPassword: string;
  loading: boolean;
  error: string | null;
  setNewPassword: (password: string) => void;
  setConfirmPassword: (password: string) => void;
  submitPassword: () => Promise<boolean>;
  clearError: () => void;
  hasValidSession: boolean;
}

/**
 * Custom hook for update password flow (NEW_PASSWORD_REQUIRED challenge)
 */
export const useUpdatePassword = (): UseUpdatePasswordReturn => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [session, setSession] = useState<UpdatePasswordSession | null>(null);

  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  // Check for valid session on mount
  useEffect(() => {
    const storedSessionJson = sessionStorage.getItem('updatePasswordSession');
    if (!storedSessionJson) {
      setError('Session information is missing or invalid. Please log in again.');
      setHasValidSession(false);
      return;
    }

    try {
      const storedSession: UpdatePasswordSession = JSON.parse(storedSessionJson);
      if (storedSession.username && storedSession.session) {
        setSession(storedSession);
        setHasValidSession(true);
      } else {
        setError('Invalid session data. Please log in again.');
        setHasValidSession(false);
      }
    } catch (e) {
      console.error('Parsing stored session data failed:', e);
      setError('Invalid session data. Please log in again.');
      setHasValidSession(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const submitPassword = useCallback(async (): Promise<boolean> => {
    if (!session) {
      setError('Session information is missing. Please log in again.');
      navigate('/signin');
      return false;
    }

    if (!newPassword) {
      setError('Please enter a new password');
      return false;
    }

    // Validate password requirements
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError('Password does not meet all requirements');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await AuthService.updatePassword(
        session.session,
        session.username,
        newPassword
      );

      // Clear session after successful update
      sessionStorage.removeItem('updatePasswordSession');

      // Store tokens
      handleTokenStorage(response);

      // Fetch user data and navigate
      const result = await dispatch(fetchUserData());
      if (result?.payload?.role) {
        const navigationPath = getNavigationPath(result.payload.role);
        navigate(navigationPath);
      } else {
        navigate('/signin');
      }

      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to update password. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [session, newPassword, confirmPassword, navigate, dispatch]);

  return {
    newPassword,
    confirmPassword,
    loading,
    error,
    setNewPassword,
    setConfirmPassword,
    submitPassword,
    clearError,
    hasValidSession,
  };
};
